import { useCallback, useEffect } from "react";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

import { useCurrentConversationId } from "@/hooks/conversation";

export type SuggestionInputControl = {
  type: "suggestions";
  suggestions: string[];
  lockInput?: boolean;
};

export type MultipleChoiceInputControl = {
  type: "multiple_choice";
  suggestions: string[];
  selected: string[];
};

type ConversationState = {
  text: string;
  inputControl: null | SuggestionInputControl | MultipleChoiceInputControl;
  isThinking: boolean;
  isRecording: boolean;
  isTranscribing: boolean;
  attachmentCount: number;
  attachmentSize: number;
};

interface ConversationStore {
  conversations: Record<string, ConversationState>;
  setText: (conversationId: string, text: string) => void;
  pressChip: (conversationId: string, chip: string) => void;
  deleteLastChip: (conversationId: string) => void;
  setInputControl: (
    conversationId: string,
    inputControl: SuggestionInputControl | MultipleChoiceInputControl,
  ) => void;
  clearInputControl: (conversationId: string) => void;
  setIsThinking: (conversationId: string, isThinking: boolean) => void;
  setIsTranscribing: (conversationId: string, isTranscribing: boolean) => void;
  setIsRecording: (conversationId: string, is: boolean) => void;
  setAttachmentCount: (conversationId: string, count: number) => void;
  setAttachmentSize: (conversationId: string, size: number) => void;
  resetConversation: (conversationId: string) => void;
  initializeConversation: (conversationId: string) => void;
  resetAll: () => void;
}

const createDefaultConversationState = (): ConversationState => ({
  text: "",
  inputControl: null,
  isThinking: false,
  isTranscribing: false,
  isRecording: false,
  attachmentCount: 0,
  attachmentSize: 0,
});

export const useConversationStore = create<ConversationStore>()((set) => ({
  conversations: {},

  setText: (conversationId, text) =>
    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...state.conversations[conversationId],
          text,
        },
      },
    })),

  setIsThinking: (conversationId, isThinking) =>
    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...state.conversations[conversationId],
          isThinking,
        },
      },
    })),

  setIsTranscribing: (conversationId, isTranscribing) =>
    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...state.conversations[conversationId],
          isTranscribing,
        },
      },
    })),

  setIsRecording: (conversationId, isRecording) =>
    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...state.conversations[conversationId],
          isRecording,
        },
      },
    })),

  setAttachmentCount: (conversationId, attachmentCount) =>
    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...state.conversations[conversationId],
          attachmentCount,
        },
      },
    })),

  setAttachmentSize: (conversationId, attachmentSize) =>
    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...state.conversations[conversationId],
          attachmentSize,
        },
      },
    })),

  setInputControl: (conversationId, inputControl) =>
    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...state.conversations[conversationId],
          inputControl,
        },
      },
    })),

  clearInputControl: (conversationId) =>
    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...state.conversations[conversationId],
          inputControl: null,
        },
      },
    })),

  deleteLastChip: (conversationId) =>
    set((state) => {
      const conversation = state.conversations[conversationId];

      if (
        conversation.inputControl?.type !== "multiple_choice" ||
        conversation.inputControl.selected.length === 0
      ) {
        return state;
      }

      const lastChip =
        conversation.inputControl.selected[
          conversation.inputControl.selected.length - 1
        ];

      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...conversation,
            inputControl: {
              ...conversation.inputControl,
              selected: conversation.inputControl.selected.slice(0, -1),
              suggestions: [...conversation.inputControl.suggestions, lastChip],
            },
          },
        },
      };
    }),

  pressChip: (conversationId, chip) => {
    set((state) => {
      const conversation = state.conversations[conversationId];

      if (conversation.inputControl?.type !== "multiple_choice") {
        return state;
      }

      if (!conversation.inputControl.suggestions.includes(chip)) {
        return {
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...conversation,
              inputControl: {
                ...conversation.inputControl,
                suggestions: [...conversation.inputControl.suggestions, chip],
                selected: conversation.inputControl.selected.filter(
                  (s) => s !== chip,
                ),
              },
            },
          },
        };
      }

      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...conversation,
            inputControl: {
              ...conversation.inputControl,
              selected: [...conversation.inputControl.selected, chip],
              suggestions: conversation.inputControl.suggestions.filter(
                (s) => s !== chip,
              ),
            },
          },
        },
      };
    });
  },

  initializeConversation: (conversationId: string) =>
    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: createDefaultConversationState(),
      },
    })),

  resetConversation: (conversationId) =>
    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: createDefaultConversationState(),
      },
    })),

  resetAll: () => set({ conversations: {} }),
}));

export interface ConversationComposition {
  text: string;
  setText: (text: string, conversationId?: string | undefined) => void;
  inputControl: SuggestionInputControl | MultipleChoiceInputControl | null;
  setInputControl: (
    control: SuggestionInputControl | MultipleChoiceInputControl,
    conversationId?: string | undefined,
  ) => void;
  clearInputControl: (conversationId?: string | undefined) => void;
  isThinking: boolean;
  setIsThinking: (
    isThinking: boolean,
    conversationId?: string | undefined,
  ) => void;
  isTranscribing: boolean;
  setIsTranscribing: (
    isTranscribing: boolean,
    conversationId?: string | undefined,
  ) => void;
  isRecording: boolean;
  setIsRecording: (
    isRecording: boolean,
    conversationId?: string | undefined,
  ) => void;
  attachmentCount: number;
  setAttachmentCount: (
    count: number,
    conversationId?: string | undefined,
  ) => void;
  attachmentSize: number;
  setAttachmentSize: (
    size: number,
    conversationId?: string | undefined,
  ) => void;
  pressChip: (chip: string, conversationId?: string | undefined) => void;
  deleteLastChip: (conversationId?: string | undefined) => void;
  reset: (conversationId?: string | undefined) => void;
  resetAll: () => void;
}

export const useConversationComposition = (): ConversationComposition => {
  const actions = useConversationStore(
    useShallow((state) => ({
      setText: state.setText,
      setInputControl: state.setInputControl,
      clearInputControl: state.clearInputControl,
      setIsThinking: state.setIsThinking,
      setIsTranscribing: state.setIsTranscribing,
      setIsRecording: state.setIsRecording,
      setAttachmentCount: state.setAttachmentCount,
      setAttachmentSize: state.setAttachmentSize,
      pressChip: state.pressChip,
      deleteLastChip: state.deleteLastChip,
      resetConversation: state.resetConversation,
      initializeConversation: state.initializeConversation,
      resetAll: state.resetAll,
    })),
  );

  const conversations = useConversationStore((state) => state.conversations);

  // Set default conversationId for new conversation case
  // Text state should be the only thing set on default conversationId
  const currentConversationId = useCurrentConversationId() ?? "default";

  useEffect(() => {
    if (!conversations[currentConversationId]) {
      actions.initializeConversation(currentConversationId);
    }
  }, [conversations, currentConversationId, actions]);

  const text = useConversationStore((state) => {
    const { conversations } = state;
    return conversations[currentConversationId]?.text ?? "";
  });

  const isThinking = useConversationStore((state) => {
    const { conversations } = state;
    return conversations[currentConversationId]?.isThinking ?? false;
  });

  const isTranscribing = useConversationStore((state) => {
    const { conversations } = state;
    return conversations[currentConversationId]?.isTranscribing ?? false;
  });

  const isRecording = useConversationStore((state) => {
    const { conversations } = state;
    return conversations[currentConversationId]?.isRecording ?? false;
  });

  const attachmentCount = useConversationStore((state) => {
    const { conversations } = state;
    return conversations[currentConversationId]?.attachmentCount ?? 0;
  });

  const attachmentSize = useConversationStore((state) => {
    const { conversations } = state;
    return conversations[currentConversationId]?.attachmentSize ?? 0;
  });

  const inputControl = useConversationStore((state) => {
    const { conversations } = state;
    return conversations[currentConversationId]?.inputControl ?? null;
  });

  const setText = useCallback(
    (text: string, conversationId?: string) => {
      return actions.setText(conversationId ?? currentConversationId, text);
    },
    [currentConversationId, actions.setText],
  );

  const setInputControl = useCallback(
    (
      control: SuggestionInputControl | MultipleChoiceInputControl,
      conversationId?: string,
    ) =>
      actions.setInputControl(
        conversationId ?? currentConversationId!,
        control,
      ),
    [currentConversationId, actions.setInputControl],
  );

  const clearInputControl = useCallback(
    (conversationId?: string) =>
      actions.clearInputControl(conversationId ?? currentConversationId!),
    [currentConversationId, actions.clearInputControl],
  );

  const setIsThinking = useCallback(
    (isThinking: boolean, conversationId?: string) =>
      actions.setIsThinking(
        conversationId ?? currentConversationId!,
        isThinking,
      ),
    [currentConversationId, actions.setIsThinking],
  );

  const setIsTranscribing = useCallback(
    (isTranscribing: boolean, conversationId?: string) =>
      actions.setIsTranscribing(
        conversationId ?? currentConversationId!,
        isTranscribing,
      ),
    [currentConversationId, actions.setIsTranscribing],
  );

  const setIsRecording = useCallback(
    (isRecording: boolean, conversationId?: string) =>
      actions.setIsRecording(
        conversationId ?? currentConversationId!,
        isRecording,
      ),
    [currentConversationId, actions.setIsRecording],
  );

  const setAttachmentCount = useCallback(
    (count: number, conversationId?: string) =>
      actions.setAttachmentCount(
        conversationId ?? currentConversationId!,
        count,
      ),
    [currentConversationId, actions.setAttachmentCount],
  );

  const setAttachmentSize = useCallback(
    (size: number, conversationId?: string) =>
      actions.setAttachmentSize(conversationId ?? currentConversationId!, size),
    [currentConversationId, actions.setAttachmentSize],
  );

  const pressChip = useCallback(
    (chip: string, conversationId?: string) =>
      actions.pressChip(conversationId ?? currentConversationId!, chip),
    [currentConversationId, actions.pressChip],
  );

  const deleteLastChip = useCallback(
    (conversationId?: string) =>
      actions.deleteLastChip(conversationId ?? currentConversationId!),
    [currentConversationId, actions.deleteLastChip],
  );

  const reset = useCallback(
    (conversationId?: string) =>
      actions.resetConversation(conversationId ?? currentConversationId!),
    [currentConversationId, actions.resetConversation],
  );

  return {
    text,
    inputControl,
    isThinking,
    isTranscribing,
    isRecording,
    attachmentCount,
    attachmentSize,
    setText,
    setInputControl,
    clearInputControl,
    setIsThinking,
    setIsTranscribing,
    setIsRecording,
    setAttachmentCount,
    setAttachmentSize,
    pressChip,
    deleteLastChip,
    reset,
    resetAll: actions.resetAll,
  };
};
